'use client';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Download, Plus, X } from 'lucide-react';
import Button, { LinkButton } from '@/components/ui/Button';
import { analytics } from '@/lib/analytics';
import { ToneAndChildren, TrackingEvent } from '@/utils/helpers';
import { GEAR } from '@/utils/constants';
import { useTranslations } from 'next-intl';
import LoadingDots from '../LoadingDots';
import Checkbox from '../Checkbox/Checkbox';
import { GearIcon } from '@/components/icons/Gear';

export interface DownloadTriggerProps {
  onClick: () => void;
  isDownloading: boolean;
  disabled: boolean;
}

interface Props {
  tracking?: TrackingEvent;
  tone: ToneAndChildren;
  onDownload: () => void;
  isDownloading: boolean;
  downloadCount: number;
  continueText?: string;
  onDontShowAgain?: () => void;
  isApiSelect?: boolean;
  /** Custom trigger(s). Used by tone Actions to render the primary CTA + count. */
  renderTrigger?: (props: DownloadTriggerProps) => ReactNode;
}

const primaryLabel = (isApiSelect: boolean) => (isApiSelect ? 'Select Tone' : 'Download Tone');

export const ButtonPlain = ({
  tone,
  onDownload,
  tracking,
  isDownloading,
  continueText,
  isApiSelect = false,
}: Props) => {
  const label = primaryLabel(isApiSelect);
  return (
    <Button
      className={`min-h-[50px] ${continueText ? 'w-full' : 'w-full sm:w-[256px]'}`}
      variant="slim"
      disabled={!tone.models?.some((model) => model.model_url)}
      onClick={onDownload}
      isPrimary={true}
      tracking={tracking}
      aria-label={continueText || label}
    >
      {isDownloading ? (
        <LoadingDots />
      ) : (
        <>
          {!continueText && (isApiSelect ? <Plus size={20} /> : <Download size={20} />)}
          {continueText || label}
        </>
      )}
    </Button>
  );
};

export const ButtonModal = ({
  tracking,
  tone,
  onDownload,
  isDownloading,
  downloadCount,
  onDontShowAgain,
  isApiSelect = false,
  renderTrigger,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const [isChecked, setIsChecked] = useState(false);
  const t = useTranslations('downloadModal');
  const noModels = !tone.models?.some((model) => model.model_url);

  const toggleModal = () => {
    setIsOpen(!isOpen);
    if (tracking && isOpen) {
      analytics.capture(tracking.event, tracking.properties);
    }
  };

  const openModal = () => {
    analytics.capture('tone.ir_download_modal.opened', {
      from: 'actions',
    });
    toggleModal();
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isChecked) {
      onDontShowAgain?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChecked]);

  return (
    <>
      {renderTrigger ? (
        renderTrigger({
          onClick: openModal,
          isDownloading,
          disabled: noModels,
        })
      ) : (
        <ButtonPlain
          tone={tone}
          tracking={{
            event: 'tone.ir_download_modal.opened',
            properties: {
              from: 'actions',
            },
          }}
          onDownload={toggleModal}
          isDownloading={isDownloading}
          downloadCount={downloadCount}
          isApiSelect={isApiSelect}
        />
      )}
      {isOpen && (
        <span className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
          <span
            ref={modalRef}
            className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-xl rounded-xl shadow-xl flex flex-col p-8"
          >
            <span className="flex justify-between items-start gap-4">
              <span className="text-xl font-semibold">{t('title')}</span>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </span>
            <span className="mt-4 text-zinc-400 text-left">{t('body')}</span>
            <span className="mt-12 text-zinc-400 text-left flex gap-4 items-center">
              <GearIcon gear={GEAR.AMP} />
              +
              <GearIcon gear={GEAR.CAB} />
              =
              <GearIcon gear={GEAR.AMP_CAB} />
            </span>
            <Checkbox
              className="mt-12"
              label={t('dontShowAgain')}
              checked={isChecked}
              onChange={setIsChecked}
            />
            <span className={'mt-8 flex flex-col md:flex-row gap-4'}>
              {!isApiSelect && (
                <LinkButton
                  className={'w-full'}
                  isSecondary={true}
                  variant={'slim'}
                  href={'/search?gears=cab'}
                  target="_blank"
                >
                  {t('browseCabinets')}
                </LinkButton>
              )}
              <ButtonPlain
                tone={tone}
                tracking={tracking}
                onDownload={() => {
                  onDownload();
                  toggleModal();
                }}
                isDownloading={isDownloading}
                downloadCount={downloadCount}
                continueText={t('continueDownload')}
                isApiSelect={isApiSelect}
              />
            </span>
          </span>
        </span>
      )}
    </>
  );
};

export const DownloadButton = ({
  tone,
  tracking,
  onDownload,
  isDownloading,
  downloadCount,
  isApiSelect = false,
  renderTrigger,
}: Props) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const noModels = !tone.models?.some((model) => model.model_url);
  useEffect(() => {
    try {
      // Check if dontShowAgain is set in localStorage
      const dontShowAgain = localStorage.getItem('irMessageDontShowAgain');
      if (dontShowAgain) {
        setDontShowAgain(true);
      }
    } catch (error) {
      console.error(error);
    }
  }, []);
  if (tone.gear === GEAR.AMP && !dontShowAgain) {
    return (
      <ButtonModal
        tone={tone}
        tracking={tracking}
        onDownload={onDownload}
        isDownloading={isDownloading}
        downloadCount={downloadCount}
        onDontShowAgain={() => {
          try {
            localStorage.setItem('irMessageDontShowAgain', 'true');
          } catch (error) {
            console.error(error);
          }
        }}
        isApiSelect={isApiSelect}
        renderTrigger={renderTrigger}
      />
    );
  }

  if (renderTrigger) {
    return (
      <>
        {renderTrigger({
          onClick: () => {
            if (tracking) {
              analytics.capture(tracking.event, tracking.properties);
            }
            onDownload();
          },
          isDownloading,
          disabled: noModels,
        })}
      </>
    );
  }

  return (
    <ButtonPlain
      tone={tone}
      tracking={tracking}
      onDownload={onDownload}
      isDownloading={isDownloading}
      downloadCount={downloadCount}
      isApiSelect={isApiSelect}
    />
  );
};
